import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Heart, Minus, Plus, Ruler } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { CountdownTimer } from '@/components/CountdownTimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const { addToCart } = useCart();
  const { isAuthenticated, token } = useAuth();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      if (response.data.sizes?.length > 0) {
        setSelectedSize(response.data.sizes[0]);
      }
      if (response.data.colors?.length > 0) {
        setSelectedColor(response.data.colors[0]);
      }
      
      if (isAuthenticated) {
        const wishlistRes = await axios.get(`${API}/wishlist`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsInWishlist(wishlistRes.data.some(p => p.id === id));
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }
    if (!selectedColor) {
      toast.error('Please select a color');
      return;
    }
    
    const success = await addToCart(product.id, quantity, selectedSize, selectedColor);
    if (success) {
      toast.success('Added to cart');
    } else {
      toast.error('Failed to add to cart');
    }
  };

  const handleWishlist = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to wishlist');
      return;
    }

    try {
      if (isInWishlist) {
        await axios.delete(`${API}/wishlist/remove/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/add`, { product_id: id }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsInWishlist(true);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="pt-24 md:pt-28 min-h-screen container-custom">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="aspect-square bg-neutral-900 animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-neutral-900 animate-pulse w-3/4" />
            <div className="h-6 bg-neutral-900 animate-pulse w-1/4" />
            <div className="h-20 bg-neutral-900 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-24 md:pt-28 min-h-screen container-custom text-center">
        <h1 className="font-['Syne'] text-2xl font-bold">Product not found</h1>
        <Link to="/shop" className="text-[#FF3B30] mt-4 inline-block">
          Back to Shop
        </Link>
      </div>
    );
  }

  const isDropNotYetAvailable = product.is_drop && product.drop_date && new Date(product.drop_date) > new Date();

  return (
    <div className="pt-24 md:pt-28 min-h-screen" data-testid="product-page">
      <div className="container-custom">
        {/* Breadcrumb */}
        <Link 
          to="/shop" 
          className="inline-flex items-center space-x-2 text-muted-foreground hover:text-white mb-8 transition-colors"
          data-testid="back-to-shop"
        >
          <ChevronLeft size={16} />
          <span className="uppercase text-xs tracking-widest">Back to Shop</span>
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square border border-neutral-800 overflow-hidden">
              <img
                src={product.images?.[selectedImage] || 'https://via.placeholder.com/600'}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>
            {product.images?.length > 1 && (
              <div className="flex space-x-4">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 border overflow-hidden ${
                      selectedImage === idx ? 'border-white' : 'border-neutral-800'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            {product.is_drop && (
              <span className="inline-block bg-[#FF3B30] text-white text-xs uppercase tracking-widest px-3 py-1 mb-4">
                Limited Drop
              </span>
            )}
            
            <h1 className="font-['Syne'] text-3xl md:text-4xl font-bold mb-2" data-testid="product-name">
              {product.name}
            </h1>
            
            <p className="text-muted-foreground uppercase text-xs tracking-widest mb-4">
              {product.category} {product.subcategory && `/ ${product.subcategory}`}
            </p>
            
            <p className="text-2xl font-bold mb-6" data-testid="product-price">
              {formatPrice(product.price)}
            </p>

            {isDropNotYetAvailable && (
              <div className="mb-6 p-4 border border-neutral-800 bg-neutral-900/50">
                <p className="text-sm text-muted-foreground mb-3">Drops in:</p>
                <CountdownTimer targetDate={product.drop_date} />
              </div>
            )}
            
            <p className="text-muted-foreground mb-8 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {/* Color Selection */}
            {product.colors?.length > 0 && (
              <div className="mb-6">
                <label className="block uppercase text-xs tracking-widest mb-3">
                  Color: <span className="text-muted-foreground">{selectedColor}</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 border text-sm ${
                        selectedColor === color
                          ? 'border-white bg-white text-black'
                          : 'border-neutral-700 hover:border-white'
                      }`}
                      data-testid={`color-${color.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            {product.sizes?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="uppercase text-xs tracking-widest">
                    Size: <span className="text-muted-foreground">{selectedSize}</span>
                  </label>
                  <Link 
                    to="/size-guide" 
                    className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-white"
                    data-testid="size-guide-link"
                  >
                    <Ruler size={14} />
                    <span>Size Guide</span>
                  </Link>
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`w-12 h-12 border text-sm ${
                        selectedSize === size
                          ? 'border-white bg-white text-black'
                          : 'border-neutral-700 hover:border-white'
                      }`}
                      data-testid={`size-${size.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-8">
              <label className="block uppercase text-xs tracking-widest mb-3">Quantity</label>
              <div className="flex items-center border border-neutral-700 w-fit">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 hover:bg-neutral-800"
                  data-testid="quantity-decrease"
                >
                  <Minus size={16} />
                </button>
                <span className="w-12 text-center" data-testid="quantity-value">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-3 hover:bg-neutral-800"
                  data-testid="quantity-increase"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-4">
              <Button
                onClick={handleAddToCart}
                disabled={isDropNotYetAvailable}
                className="btn-primary rounded-none flex-1"
                data-testid="add-to-cart-btn"
              >
                {isDropNotYetAvailable ? 'Coming Soon' : 'Add to Cart'}
              </Button>
              <Button
                onClick={handleWishlist}
                variant="outline"
                className="btn-outline rounded-none px-4"
                data-testid="wishlist-btn"
              >
                <Heart size={20} fill={isInWishlist ? '#FF3B30' : 'none'} className={isInWishlist ? 'text-[#FF3B30]' : ''} />
              </Button>
            </div>

            {/* Stock info */}
            {product.stock <= 10 && product.stock > 0 && (
              <p className="text-[#FF3B30] text-sm mt-4">
                Only {product.stock} left in stock
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
